import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import HeaderBar from 'components/ui/HeaderBar';
import RoleBasedSidebar from 'components/ui/RoleBasedSidebar';
import BreadcrumbNavigation from 'components/ui/BreadcrumbNavigation';
import Icon from 'components/AppIcon';
import StatusIndicatorBadge from 'components/ui/StatusIndicatorBadge';
import { useAuth } from 'context/AuthContext';
import { employerAPI } from 'utils/api';
import { formatDate } from 'utils/dateFormatter';

const EmployerSpecialQuotaDetails = () => {
  const navigate = useNavigate();
  const { vdrId } = useParams();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user && (!user.eclabUser || user.role !== 3)) {
      navigate('/login-screen');
    }
  }, [user, navigate]);

  useEffect(() => {
    const loadQuota = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await employerAPI.getSpecialQuota(vdrId);
        setQuota(data);
      } catch (err) {
        console.error('Failed to load special quota', err);
        setError(err.response?.data?.error || err.message || 'Failed to load quota.');
      } finally {
        setLoading(false);
      }
    };

    loadQuota();
  }, [vdrId]);

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const reservationLimit = useMemo(() => {
    const quotaApproved = quota?.quotaApproved || quota?.quota_approved || 0;
    return quotaApproved * 2;
  }, [quota]);

  const selectionLimit = useMemo(() => quota?.quotaApproved || quota?.quota_approved || 0, [quota]);

  const statusLabel = useMemo(() => {
    const status = (quota?.status || '').toLowerCase();
    if (status === 'ready_for_selection') return 'Ready for Selection';
    if (status === 'pending_quota_review') return 'Pending Admin Review';
    if (status === 'selection_submitted') return 'Selection Submitted';
    if (status === 'selection_approved') return 'Selection Approved';
    if (status === 'selection_rejected') return 'Selection Rejected';
    return status || 'Draft';
  }, [quota?.status]);

  const canSelectWorkers = (quota?.status || '').toLowerCase() === 'ready_for_selection';

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <HeaderBar userRole="employer" onSidebarToggle={handleSidebarToggle} userName={user?.fullName || 'Employer'} />
        <RoleBasedSidebar userRole="employer" isCollapsed={sidebarCollapsed} onToggle={handleSidebarToggle} />
        <main className={`transition-all duration-300 ease-smooth pt-16 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
          <div className="p-8 flex justify-center items-center min-h-[400px]">
            <Icon name="Loader" size={48} className="animate-spin text-primary" />
          </div>
        </main>
      </div>
    );
  }

  if (error || !quota) {
    return (
      <div className="min-h-screen bg-background">
        <HeaderBar userRole="employer" onSidebarToggle={handleSidebarToggle} userName={user?.fullName || 'Employer'} />
        <RoleBasedSidebar userRole="employer" isCollapsed={sidebarCollapsed} onToggle={handleSidebarToggle} />
        <main className={`transition-all duration-300 ease-smooth pt-16 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
          <div className="p-8 max-w-3xl mx-auto">
            <div className="bg-error-50 border border-error-200 text-error p-6 rounded-lg text-center">
              <Icon name="AlertCircle" size={32} className="mx-auto mb-3" />
              {error || 'Special quota not found.'}
              <div className="mt-4">
                <button
                  onClick={() => navigate('/employer-application-management')}
                  className="inline-flex items-center px-4 py-2 bg-secondary-100 text-text-primary rounded-lg hover:bg-secondary-200"
                >
                  <Icon name="ArrowLeft" size={16} className="mr-2" />
                  Back to Applications
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <HeaderBar userRole="employer" onSidebarToggle={handleSidebarToggle} userName={user?.fullName || 'Employer'} />
      <RoleBasedSidebar userRole="employer" isCollapsed={sidebarCollapsed} onToggle={handleSidebarToggle} />

      <main className={`transition-all duration-300 ease-smooth pt-16 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
        <div className="p-6 max-w-6xl mx-auto space-y-6">
          <BreadcrumbNavigation userRole="employer" />

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-text-primary">{quota.vdrId || vdrId}</h1>
              <p className="text-text-secondary">{quota.companyName || 'Special Quota VDR'}</p>
              <div className="text-xs text-text-secondary mt-1">Quota Reference: {quota.quotaReferenceNo || quota.quota_reference_no || '—'}</div>
            </div>
            <div className="flex items-center gap-3">
              <StatusIndicatorBadge status={canSelectWorkers ? 'approved' : 'default'} label={statusLabel} />
              {canSelectWorkers && (
                <button
                  onClick={() => navigate('/employer-biodata-selection', { state: { vdrApplication: quota, fromApplications: true } })}
                  className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Icon name="Users" size={16} className="mr-2" />
                  Select Workers
                </button>
              )}
            </div>
          </div>

          {!canSelectWorkers && (
            <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4 text-sm text-text-secondary">
              <div className="flex items-center gap-2 text-text-primary font-semibold mb-1">
                <Icon name="Info" size={16} />
                Selection locked
              </div>
              This special quota is awaiting admin review. Once approved with receipt, worker selection will be unlocked.
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-secondary-200 rounded-lg p-5 shadow-sm space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">Quota Details</h3>
              <dl className="grid grid-cols-2 gap-4 text-sm text-text-secondary">
                <div>
                  <dt className="font-medium text-text-primary">Nationality</dt>
                  <dd className="text-text-primary">{quota.nationality || '—'}</dd>
                </div>
                <div>
                  <dt className="font-medium text-text-primary">Approved Date</dt>
                  <dd className="text-text-primary">{formatDate(quota.approvedDate || quota.approved_date)}</dd>
                </div>
                <div>
                  <dt className="font-medium text-text-primary">Quota Approved</dt>
                  <dd className="text-text-primary">{quota.quotaApproved || quota.quota_approved || '—'}</dd>
                </div>
                <div>
                  <dt className="font-medium text-text-primary">Reservation Limit</dt>
                  <dd className="text-text-primary">{reservationLimit || '—'}</dd>
                </div>
                <div>
                  <dt className="font-medium text-text-primary">Selection Limit</dt>
                  <dd className="text-text-primary">{selectionLimit || '—'}</dd>
                </div>
              </dl>
            </div>

            <div className="bg-white border border-secondary-200 rounded-lg p-5 shadow-sm space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">Documents</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <div className="text-text-secondary">
                    <div className="font-medium text-text-primary">Quota Slip</div>
                    <div className="text-xs text-text-tertiary">Private — viewable by admin</div>
                  </div>
                  {quota.quotaSlipUrl || quota.quota_slip_url ? (
                    <a
                      href={quota.quotaSlipUrl || quota.quota_slip_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center px-3 py-1.5 bg-secondary-100 text-text-primary rounded-lg hover:bg-secondary-200"
                    >
                      <Icon name="ExternalLink" size={14} className="mr-1" />
                      View
                    </a>
                  ) : (
                    <span className="text-text-tertiary">Not uploaded</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-text-secondary">
                    <div className="font-medium text-text-primary">Receipt</div>
                    <div className="text-xs text-text-tertiary">Provided by admin after approval</div>
                  </div>
                  {quota.receiptUrl || quota.receipt_url ? (
                    <a
                      href={quota.receiptUrl || quota.receipt_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center px-3 py-1.5 bg-secondary-100 text-text-primary rounded-lg hover:bg-secondary-200"
                    >
                      <Icon name="ExternalLink" size={14} className="mr-1" />
                      View
                    </a>
                  ) : (
                    <span className="text-text-tertiary">Pending</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {quota.remarks && (
            <div className="bg-white border border-secondary-200 rounded-lg p-5 shadow-sm">
              <h3 className="text-lg font-semibold text-text-primary mb-2">Remarks</h3>
              <p className="text-sm text-text-secondary whitespace-pre-wrap">{quota.remarks}</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default EmployerSpecialQuotaDetails;
